import React, { useEffect } from 'react';
import moment from 'moment';
import '@trp-beacon-ui-kit/foundation/style-tokens/css/variables.css';
import '@trp-beacon-ui-kit/foundation/fonts/fonts.css';
import { Box, Grid, Typography } from '@trp-individual-investor-adv-svc/ii-prosp-components';

import { PsBeaconButton, PsBeaconIcon } from '../shared/BeaconRegister';
import { Content } from '../shared/Content';
import PastPlanTile from './Tiles/PastPlanTile';
import HubPageBase from './HubPageBase';
import UpdateQuestionnaireTile from './Tiles/UpdateQuestionnaireTile';
import { log, scrollToTop, setLastRequestTime } from '../../Utils';
import AppointmentTile from './Tiles/AppointmentTile';
import GenericError from '../shared/Errors/GenericError';
import Loading from '../../containers/Loading';
import InsightsTile from './Tiles/InsightsTile';
import LoadingSpinner from '../../containers/LoadingSpinner';
import AddPartnerTile from './Tiles/AddPartnerTile';
import { getActiveActions, getAppointmentDetails } from '../shared/ApiCalls/ApiCalls';
import Calendaricon from '../../assets/images/CalendarIcon';

const AppointmentPage = ({ state, dispatch }) => {
    const [serviceError, setServiceError] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [buttonSize, setButtonSize] = React.useState(
        window.innerWidth <= 960 ? '100%' : 'inherit'
    );
    const [open, setOpen] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(true);
    const hasScheduledAppt = state?.invitations?.PlanReview?.invitation ?? false;
    const reschedulingLink = state?.invitations?.PlanReview?.invitation?.reschedulingLink ?? null;
    const addPartnerConditions = state?.numberOfClients < 2 && state?.isPrimary;
    const showAddPartnerAndRescheduling = addPartnerConditions && reschedulingLink;
    const documentsTilesSize = () => {
        if ((reschedulingLink && !addPartnerConditions) || showAddPartnerAndRescheduling) {
            return 3;
        }
        if (!reschedulingLink && addPartnerConditions) {
            return 4;
        }
        return 4;
    };
    const viewPlanJustifyCond = () => {
        if (reschedulingLink && !addPartnerConditions) {
            return 'flex-end';
        }
        if (!reschedulingLink && addPartnerConditions) {
            return 'center';
        }
        return 'flex-start';
    };

    const isProspClient = state?.planningGroup?.activeEngagement?.engagementStatuses?.some(
        (x) => x.status === 'NEWP'
    );
    const isProspCompleted = state?.planningGroup?.activeEngagement?.engagementStatuses?.some(
        (x) => x.status === 'FPAC'
    );

    const showQuestionnaireTile = () => {
        return !isProspClient || isProspCompleted;
    };

    const popupAlertText = Content.planBlockedAlert.contentText;
    const popupAlertTitle = Content.planBlockedAlert.contentTitle;
    const getTitleText = hasScheduledAppt
        ? `You're all set for your upcoming appointment`
        : 'Schedule an appointment with a financial advisor to review your plan';
    const getApptCardDateText = state?.invitations?.PlanReview?.startTime
        ? moment(state?.invitations?.PlanReview?.startTime).format('dddd, MMMM Do YYYY')
        : 'There was a problem retrieving your appointment information';

    const getApptCardTimeframe = state?.invitations?.PlanReview?.startTime
        ? `${moment(state?.invitations?.PlanReview?.startTime).format('hh:mm A')} - ${moment(
              state?.invitations?.PlanReview?.endTime
          ).format('hh:mm A')}`
        : 'Please try refreshing your page to receive appointment information';

    const ttOutageMessage =
        'We are having trouble connecting to our scheduling system at this time.';

    useEffect(() => {
        const smallScreenSize = window.matchMedia('(max-width: 960px)');
        const handleResize = () => {
            setButtonSize(smallScreenSize.matches ? '100%' : 'inherit');
        };
        smallScreenSize.addEventListener('change', handleResize);
        return () => smallScreenSize.removeEventListener('change', handleResize);
    }, []);

    const handlePopupClose = () => {
        setOpen(false);
    };

    const rescheduleAppt = () => {
        log(console.log('reschedule appt -------------------'));
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 2000);
        if (reschedulingLink) {
            window.open(reschedulingLink, '_blank');
        }
    };

    const redirectToTimeTrade = () => {
        setLastRequestTime();
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 2000);
        if (!state.schedulingLink) {
            // calendar service error
            log(console.log('service error, !state.schedulingLink', state.schedulingLink));
        } else {
            log(console.log('redirect to APSC ------------------------', state.schedulingLink));
            // submit from data to timetrade
            window.open(state.schedulingLink, '_blank');
        }
    };

    useEffect(() => {
        function handleVisibilityChange() {
            log(console.log('Visibility change occurred!'));
            if (document.hidden) {
                log(console.log('Browser tab is hidden'));
            } else {
                scrollToTop();
                getAppointmentDetails({
                    dispatch,
                    apiKey: state?.apiKey,
                    setLoading: setInitialLoading,
                    externalGroupId: state?.planningGroup?.externalId,
                })
                    .then(() => getActiveActions({ dispatch, apiKey: state?.apiKey }))
                    .then(() => setInitialLoading(false))
                    .catch(() => setInitialLoading(false));
            }
        }

        scrollToTop();
        getAppointmentDetails({
            dispatch,
            apiKey: state?.apiKey,
            setLoading: setInitialLoading,
            externalGroupId: state?.planningGroup?.externalId,
        })
            .then(() => setInitialLoading(false))
            .catch(() => setInitialLoading(false));
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [state?.apiKey, dispatch]);

    const handleRefresh = () => {
        window.location.reload(true);
    };

    // Loader for API call
    if (initialLoading) {
        return (
            <Box sx={{ padding: { xs: '72px 0', md: '144px 0' } }}>
                <Loading headerText="Appointment content Loading" />
            </Box>
        );
    }

    return (
        <HubPageBase
            state={state}
            dispatch={dispatch}
            serviceError={serviceError}
            popUpError={open}
            popupAlertTitle={popupAlertTitle}
            popupAlertText={popupAlertText}
            popupClose={handlePopupClose}>
            <Box className="b-container-content" sx={{ inlineSize: 'unset !important' }}>
                <Grid
                    container
                    sx={{
                        width: '100%',
                        paddingTop: '64px',
                        paddingBottom: { xs: '1rem', md: '64px' },
                    }}>
                    <Grid item cols={12} lg={9}>
                        {state?.timeTradeFailure && (
                            <GenericError title={ttOutageMessage} id="service-card-error">
                                <Box sx={{ paddingBottom: '24px' }}>
                                    We are having trouble connecting to our scheduling system at
                                    this time. Refresh your page and try again
                                </Box>
                                <Box>
                                    <PsBeaconButton onclick={handleRefresh} scale="jumbo">
                                        Refresh
                                    </PsBeaconButton>
                                </Box>
                            </GenericError>
                        )}
                        {!state?.timeTradeFailure && (
                            <>
                                <Typography
                                    id="appointment-page-title"
                                    sx={{ paddingBottom: '16px' }}
                                    type="headline" serif loose scale={400}>
                                    {getTitleText}
                                </Typography>
                                <Typography
                                    id="appointment-page-content-container"
                                    sx={{ paddingBottom: '48px' }}
                                    loose light>
                                    {hasScheduledAppt ? (
                                        <Box>
                                            <Box
                                                id="appointment-page-content"
                                                sx={{ paddingBottom: '48px' }}>
                                                {Content.HubPagesContent.appScheduled.content}
                                            </Box>
                                            <Box
                                                id="appointment-page-subcontent-1"
                                                sx={{ paddingBottom: '48px' }}>
                                                {Content.HubPagesContent.appScheduled.subContent1}
                                            </Box>
                                            <Box id="appointment-page-subcontent-2">
                                                {Content.HubPagesContent.appScheduled.subContent2}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Box
                                                id="appointment-page-appt-needed-content"
                                                sx={{ paddingBottom: '48px' }}>
                                                {Content.HubPagesContent.appNeeded.content}
                                            </Box>
                                            <Box id="appointment-page-subcontent-1">
                                                {Content.HubPagesContent.appNeeded.subContent}
                                            </Box>
                                            <Box
                                                id="schedule-appointment-button-container"
                                                sx={{ paddingTop: '24px' }}>
                                                <PsBeaconButton
                                                    id="schedule-appointment-button"
                                                    onClick={!isLoading && redirectToTimeTrade}
                                                    style={{
                                                        width: buttonSize,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    disabled={state?.isCustomerWebView}
                                                    variant="primary"
                                                    scale="jumbo">
                                                    <>
                                                        {!isLoading && (
                                                            <>
                                                                Schedule an Appointment
                                                                <PsBeaconIcon
                                                                    slot="suffix"
                                                                    style={{
                                                                        marginTop: '2px ',
                                                                    }}>
                                                                    <Calendaricon />
                                                                </PsBeaconIcon>
                                                            </>
                                                        )}
                                                        {isLoading && (
                                                            <LoadingSpinner
                                                                scale="mini"
                                                                text="Loading..."
                                                            />
                                                        )}
                                                    </>
                                                </PsBeaconButton>
                                            </Box>
                                        </Box>
                                    )}
                                </Typography>
                            </>
                        )}
                    </Grid>
                    {showQuestionnaireTile() && (
                        <Grid
                            item
                            cols={12}
                            lg={3}
                            sx={{
                                justifyItems: 'end',
                                display: { xs: 'none', lg: 'inherit' },
                            }}>
                            <Box>
                                <UpdateQuestionnaireTile
                                    data-testid="update-questionnaire-tile"
                                    buttonSize={buttonSize}
                                />
                            </Box>
                        </Grid>
                    )}
                </Grid>
                <Grid
                    container
                    id="tile-container"
                    pt="64px"
                    sx={{
                        minHeight: { xs: 'inherit:', lg: '300px' },
                    }}>
                    {showQuestionnaireTile() && (
                        <Grid
                            item
                            cols={12}
                            lg={4}
                            sx={{
                                display: { xs: 'inherit', lg: 'none' },
                            }}>
                            <Box sx={{ width: { xs: '100%', md: 'inherit' } }}>
                                <UpdateQuestionnaireTile
                                    fullHeight
                                    data-testid="update-questionnaire-tile"
                                    buttonSize={buttonSize}
                                />
                            </Box>
                        </Grid>
                    )}
                    {reschedulingLink && (
                        <Grid item cols={12} lg={6}>
                            <AppointmentTile
                                buttonSize={buttonSize}
                                rescheduleAppt={!isLoading && rescheduleAppt}
                                isCustomerWebView={state?.isCustomerWebView}
                                isLoading={isLoading}
                                date={getApptCardDateText}
                                timeframe={getApptCardTimeframe}
                            />
                        </Grid>
                    )}
                    {addPartnerConditions && (
                        <Grid
                            item
                            sx={{
                                justifyItems: {
                                    lg: reschedulingLink ? 'end' : 'start',
                                },
                            }}
                            cols={12}
                            lg={showAddPartnerAndRescheduling ? 3 : 4}>
                            <AddPartnerTile
                                buttonSize={buttonSize}
                                fullHeight
                                isCustomerWebView={state?.isCustomerWebView}
                            />
                        </Grid>
                    )}
                    {state?.hasDocuments && (
                        <Grid
                            sx={{
                                justifyItems: {
                                    xs: 'inherit',
                                    lg: viewPlanJustifyCond(),
                                },
                            }}
                            item
                            cols={12}
                            lg={documentsTilesSize()}>
                            <PastPlanTile
                                state={state}
                                setServiceError={setServiceError}
                                setPopupError={setOpen}
                                fullHeight
                            />
                        </Grid>
                    )}
                    {state?.hasDocuments && (
                        <Grid
                            item
                            cols={12}
                            lg={documentsTilesSize()}
                            sx={{
                                justifyItems: {
                                    lg: showAddPartnerAndRescheduling ? 'start' : 'end',
                                },
                            }}>
                            <InsightsTile fullHeight buttonSize={buttonSize} />
                        </Grid>
                    )}
                </Grid>
            </Box>
        </HubPageBase>
    );
};

export default AppointmentPage;
